import React, { useState, useEffect, useCallback } from 'react';
import { dataEntryAPI, projectsAPI } from '../../services/api';
import { Table } from '../../components/common/Table';
import { Button } from '../../components/common/Button';
import { Modal } from '../../components/common/Modal';
import { Badge } from '../../components/common/Badge';
import { Pagination } from '../../components/common/Pagination';
import { PageHeader } from '../../components/layout/PageHeader';
import { FormField, Input, Select, Textarea } from '../../components/common/FormField';
import { PlusIcon, MagnifyingGlassIcon, PencilIcon, TrashIcon, PlusCircleIcon, MinusCircleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const EMPTY = { title:'', category:'', status:'draft', project:'', tags:'', fields:[{key:'',value:'',type:'text'}] };

export function DataEntryPage() {
  const [entries,setEntries]=useState([]); const [projects,setProjects]=useState([]); const [pg,setPg]=useState(null); const [loading,setL]=useState(true);
  const [search,setSrch]=useState(''); const [status,setSt]=useState(''); const [page,setPage]=useState(1);
  const [open,setOpen]=useState(false); const [editing,setEd]=useState(null); const [form,setForm]=useState(EMPTY); const [saving,setSv]=useState(false);

  const load = useCallback(async()=>{
    setL(true);
    try { const r=await dataEntryAPI.getAll({search,status,page,limit:12}); setEntries(r.data||[]); setPg(r.pagination); }
    catch { toast.error('Failed to load'); }
    finally { setL(false); }
  },[search,status,page]);

  useEffect(()=>{load();},[load]);
  useEffect(()=>{ projectsAPI.getAll({limit:100}).then(r=>setProjects(r.data||[])).catch(()=>{}); },[]);

  const openCreate=()=>{setEd(null);setForm(EMPTY);setOpen(true);};
  const openEdit=entry=>{setEd(entry);setForm({title:entry.title,category:entry.category,status:entry.status,project:entry.project?._id||'',tags:(entry.tags||[]).join(', '),fields:entry.fields?.length?entry.fields:[{key:'',value:'',type:'text'}]});setOpen(true);};
  const del=async id=>{if(!window.confirm('Delete?'))return;try{await dataEntryAPI.delete(id);toast.success('Deleted');load();}catch{toast.error('Failed');}};

  const save=async e=>{
    e.preventDefault();setSv(true);
    try{
      const payload={...form,tags:form.tags?form.tags.split(',').map(t=>t.trim()).filter(Boolean):[],data:Object.fromEntries(form.fields.map(f=>[f.key,f.value]))};
      editing?await dataEntryAPI.update(editing._id,payload):await dataEntryAPI.create(payload);
      toast.success(editing?'Updated':'Created');setOpen(false);load();
    }catch(err){toast.error(err.message||'Failed');}
    finally{setSv(false);}
  };

  const updField=(i,k,v)=>setForm(f=>{const fs=[...f.fields];fs[i]={...fs[i],[k]:v};return{...f,fields:fs};});
  const addField=()=>setForm(f=>({...f,fields:[...f.fields,{key:'',value:'',type:'text'}]}));
  const remField=i=>setForm(f=>({...f,fields:f.fields.filter((_,j)=>j!==i)}));
  const s=(k,v)=>setForm(f=>({...f,[k]:v}));

  const cols=[
    {header:'Entry',render:e=><div><p className="font-medium text-sm text-gray-900">{e.title}</p><p className="text-xs text-gray-400">{e.category} · {e.fields?.length||0} fields</p></div>},
    {header:'Project',render:e=><span className="text-xs text-gray-500">{e.project?.name||'—'}</span>},
    {header:'Status',render:e=><Badge status={e.status}/>},
    {header:'By',render:e=><span className="text-xs text-gray-600">{e.createdBy?.name||'—'}</span>},
    {header:'Date',render:e=><span className="text-xs text-gray-400">{format(new Date(e.createdAt),'dd MMM yyyy')}</span>},
    {header:'',render:e=><div className="flex gap-1"><button onClick={()=>openEdit(e)} className="p-1.5 hover:bg-indigo-50 rounded-lg"><PencilIcon className="w-4 h-4 text-indigo-600"/></button><button onClick={()=>del(e._id)} className="p-1.5 hover:bg-red-50 rounded-lg"><TrashIcon className="w-4 h-4 text-red-500"/></button></div>},
  ];

  return (
    <div className="space-y-5">
      <PageHeader title="Data Entry" subtitle="Structured records and forms" actions={<Button icon={PlusIcon} onClick={openCreate}>New Entry</Button>}/>
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex gap-3 flex-wrap">
          <div className="relative flex-1 min-w-44"><MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"/>
            <input value={search} onChange={e=>{setSrch(e.target.value);setPage(1);}} placeholder="Search entries..." className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"/>
          </div>
          <select value={status} onChange={e=>{setSt(e.target.value);setPage(1);}} className="px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500">
            <option value="">All Status</option>
            {['draft','submitted','approved','rejected'].map(s=><option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <Table columns={cols} data={entries} loading={loading}/>
        <Pagination pagination={pg} onPageChange={setPage}/>
      </div>
      <Modal isOpen={open} onClose={()=>setOpen(false)} title={editing?'Edit Entry':'New Entry'} size="lg">
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Title" required><Input value={form.title} onChange={e=>s('title',e.target.value)} required placeholder="Entry title"/></FormField>
            <FormField label="Category" required><Input value={form.category} onChange={e=>s('category',e.target.value)} required placeholder="Reports, Survey..."/></FormField>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Project"><Select value={form.project} onChange={e=>s('project',e.target.value)}><option value="">No project</option>{projects.map(p=><option key={p._id} value={p._id}>{p.name}</option>)}</Select></FormField>
            <FormField label="Status"><Select value={form.status} onChange={e=>s('status',e.target.value)}>{['draft','submitted','approved','rejected'].map(v=><option key={v} value={v}>{v}</option>)}</Select></FormField>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Data Fields</label>
              <button type="button" onClick={addField} className="text-indigo-600 hover:text-indigo-700"><PlusCircleIcon className="w-5 h-5"/></button>
            </div>
            <div className="space-y-2">
              {form.fields.map((field,i)=>(
                <div key={i} className="flex gap-2 items-center">
                  <Input value={field.key} onChange={e=>updField(i,'key',e.target.value)} placeholder="Field name" className="flex-1"/>
                  <Input value={field.value} onChange={e=>updField(i,'value',e.target.value)} placeholder="Value" className="flex-1"/>
                  <Select value={field.type} onChange={e=>updField(i,'type',e.target.value)} className="w-28">{['text','number','date','boolean','select'].map(t=><option key={t} value={t}>{t}</option>)}</Select>
                  {form.fields.length>1&&<button type="button" onClick={()=>remField(i)} className="text-red-400 hover:text-red-600 flex-shrink-0"><MinusCircleIcon className="w-5 h-5"/></button>}
                </div>
              ))}
            </div>
          </div>
          <FormField label="Tags" hint="Comma separated"><Input value={form.tags} onChange={e=>s('tags',e.target.value)} placeholder="finance, q1, report"/></FormField>
          <div className="flex gap-3 pt-2"><Button type="submit" loading={saving} className="flex-1">{editing?'Update':'Create'}</Button><Button type="button" variant="ghost" onClick={()=>setOpen(false)}>Cancel</Button></div>
        </form>
      </Modal>
    </div>
  );
}